import { TandemTool } from "../../base/TandemTool.js";
import { logger } from "../../../../../../core/logger.js";

export class MoveFileTool extends TandemTool {
  constructor() {
    super();
    this.toolName = "moveFileTool";
  }

  /**
   * @param {string} sourcePath
   * @param {string} destinationPath
   * @param {boolean} [overwrite=false]
   * @returns {Promise<Object>}
   */
  async moveFile(sourcePath, destinationPath, overwrite = false) {
    try {
      if (!sourcePath || typeof sourcePath !== "string") {
        return this.returnError({}, "Valid source path required");
      }
      if (!destinationPath || typeof destinationPath !== "string") {
        return this.returnError({}, "Valid destination path required");
      }

      const api = this.getTandemLocalAppApi();
      if (!api?.moveFile) {
        return this.returnError(
          {},
          "Tandem file moves require Revolt Desktop (tandemAPI.moveFile)."
        );
      }

      const root = this.getTandemWorkingDirectoryRoot();
      if (!root) {
        return this.returnError(
          {},
          "Tandem has no project folder set (working directory path)."
        );
      }

      const normalizedSourcePath = this.normalizeAndValidatePath(sourcePath);
      const normalizedDestPath = this.normalizeAndValidatePath(destinationPath);
      if (!normalizedSourcePath || !normalizedDestPath) {
        return this.returnError({}, "Invalid source or destination path");
      }

      if (normalizedSourcePath === normalizedDestPath) {
        return this.returnError(
          {},
          `Cannot move item to itself: ${sourcePath}`
        );
      }

      if (normalizedDestPath.startsWith(`${normalizedSourcePath}/`)) {
        return this.returnError(
          {},
          `Cannot move directory into itself or its subdirectories: ${sourcePath} → ${destinationPath}`
        );
      }

      const sourceRelativePath = normalizedSourcePath.replace(/^\/+/, "");
      const destRelativePath = normalizedDestPath.replace(/^\/+/, "");

      const res = await api.moveFile({
        rootPath: root,
        sourceRelativePath,
        destRelativePath,
        overwrite,
      });

      if (!res?.success) {
        return this.returnError({}, res?.error || "Failed to move on disk");
      }

      const type = res.type === "directory" ? "directory" : "file";

      logger.log(
        { tags: "tools|tandem|movefile|info", color1: "lime" },
        `Tandem moved: ${normalizedSourcePath} -> ${normalizedDestPath}`,
        { type, overwrite }
      );

      return this.returnSuccess(
        {
          source: normalizedSourcePath,
          destination: normalizedDestPath,
          type,
          overwrite,
        },
        `${type === "directory" ? "Directory" : "File"} moved successfully`
      );
    } catch (error) {
      logger.error(
        { tags: "tools|tandem|movefile|error", color1: "red" },
        `Tandem move failed: ${sourcePath} -> ${destinationPath}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }
}
